import { Fragment, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import FingerScanImg from "../assets/images/finger-scan-img.png";
import {
  api_key,
  authEmployeeID,
  sdkURL,
  secret_key,
} from "../utils/constants";

const PulmoFingerScanInstruction = () => {
  const navigate = useNavigate();
  const state = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startScan = async () => {
    setLoading(true);
    setError("");
    try {
      const scanTokenResp = await fetch(sdkURL("/vitals/create-token"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: secret_key,
        },
        body: JSON.stringify({ api_key, employee_id: authEmployeeID }),
      });
      const scanTokenRespJSON = await scanTokenResp.json();
      if (scanTokenRespJSON?.statusCode?.toString?.().startsWith("2"))
        navigate("/pulmo-finger-scan", {
          state: {
            ...state,
            scanToken: scanTokenRespJSON.scan_token,
            scanID: scanTokenRespJSON.scan_id,
          },
          replace: true,
        });
      else
        throw new Error(
          scanTokenRespJSON?.message ?? "Error in Fetching the Scan Token"
        );
    } catch (err) {
      console.error(err);
      setError(err?.message ?? "Some error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 pt-2 pb-24 relative">
      <div className="flex items-center relative mb-8 tab-screen">
        <Link
          to={-1}
          className=" w-9 h-9 bg-[#2D2D2D] rounded-full absolute left-6 flex flex-row justify-center items-center"
        >
          <img src={BackArrow} alt="" />
        </Link>
        <h2 className="text-xl font-medium text-white lg:text-2xl ml-20">
          Instructions
        </h2>
      </div>
      <div className="flex flex-col items-center justify-center relative z-50">
        <h4 className="text-xl font-bold text-secondary mb-2">
          30-Second Finger Scan
        </h4>
        <p className="text-xxs text-center font-light text-white mb-3">
          Place your finger on the camera and stay still. We'll
          <br /> analyze your lung function through a PPG signal.
        </p>
        <img src={FingerScanImg} className="w-36 mb-4" alt="" />
        <div className=" bg-[#1D1D1D]/70 rounded-[20px] w-7/12 m-auto p-6">
          <h6 className="text-base lg:text-xl font-semibold text-secondary">
            How to do Finger Scan?
          </h6>
          <p className="mb-4 text-xxs lg:text-sm font-light text-white">
            To initiate the screening process:
          </p>
          <div className="ml-0">
            <ol
              type="1"
              className="list-outside list-item text-xxs text-white font-light p-0 m-0"
            >
              <li className="mb-3 flex flex-col font-semibold text-xxs lg:text-sm">
                1. Red Screen Indication:
                <ul className="mt-2 list-disc m-0 pl-6">
                  <li className="text-[9px] lg:text-xs font-light text-white mb-1">
                    Keep a close eye on your phone's screen.
                  </li>
                  <li className="text-[9px] lg:text-xs font-light text-white mb-1">
                    When the screen turns red, it's time to proceed.
                  </li>
                </ul>
              </li>
              <li className="flex flex-col font-semibold text-xxs lg:text-sm">
                2. Finger Placement:
                <ul className="mt-2 list-disc m-0 pl-6">
                  <li className="text-[9px] lg:text-xs font-light text-white mb-1">
                    Locate the primary camera lens on the back of your phone.
                  </li>
                  <li className="text-[9px] lg:text-xs font-light text-white mb-1">
                    Gently place your index finger directly on this lens.
                  </li>
                  <li className="text-[9px] lg:text-xs font-light text-white mb-1">
                    If prompted, allow camera permission.
                  </li>
                </ul>
              </li>
            </ol>
            <p className="text-[9px] lg:text-xs font-light mt-2">
              Once your finger is correctly positioned and the screen is red,
              the screening process will automatically begin.
            </p>
          </div>
          <button
            className="mt-6 w-6/12 mx-auto flex items-center font-medium justify-center space-x-4 rounded-full outline-none px-5 py-2.5 bg-white disabled:bg-none disabled:bg-darkgray text-black mb-2"
            type="button"
            onClick={startScan}
            disabled={loading}
          >
            <span>Proceed</span>
            {loading && <Loader className="flex-shrink-0 h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="circle-blur w-[70%] h-56 mx-auto absolute top-24 left-0 right-0 blur-[100px]"></div>

      <Transition show={error.length > 0} as={Fragment}>
        <Dialog as={Fragment} onClose={() => {}}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/70 flex items-center justify-center"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col text-center">
                <Dialog.Description className="text-black font-normal text-sm">
                  {error}
                </Dialog.Description>
                <Link
                  className="mt-6 mx-auto px-4 py-2 rounded-full bg-gradient text-white text-xs font-medium"
                  to={-2}
                >
                  Back to Dashboard
                </Link>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>

      <div className="mt-6 p-5 rounded-[20px] bg-[#364560] text-white space-y-1.5 tab-screen lg:mt-6 md:mt-6">
        <h3 className="text-sm font-medium text-secondary">Disclaimer</h3>
        <p className="text-xxs text-justify font-light leading-4 text-white/80">
          For Investigational Use Only. Careplix is not a substitute for the
          clinical judgment of a health care professional. Careplix is intended
          to improve your awareness of general wellness. Careplix does not
          diagnose, treat, mitigate or prevent any disease, symptom, disorder or
          abnormal physical state. Consult with a health care professional or
          emergency services if you believe you may have a medical issue.
        </p>
      </div>
    </section>
  );
};

export default PulmoFingerScanInstruction;
